/**
 * /admin — admin tier index. The discovery surface for every admin
 * feature in the app. Renders as a responsive grid of iconed cards,
 * one per section in `ADMIN_SECTIONS`. Shipped sections are
 * clickable; unbuilt sections show with a dimmed icon and a
 * "Coming soon" badge. Sections with a `children` array (today:
 * Agents → Prompts/Models/Budgets) render a small quick-list of
 * sub-pages under the description so the discovery surface still
 * telegraphs the tree shape — even though the click target is the
 * parent's landing URL.
 *
 * Mounted at the TOP level of the route tree (sibling of
 * /workspaces/:wid/*) so a superadmin can reach pricing / runtime
 * config / cross-org analytics WITHOUT first picking a workspace.
 * Wrapped by `<RequireSuperadmin>` in App.tsx — non-superadmins
 * deep-linking here see the 403 stub.
 *
 * Reasoning on the "show unbuilt sections too" choice: the admin
 * index is the canonical place an operator looks when they want to
 * find an admin feature. Hiding unbuilt sections until they ship
 * means an operator wondering "is there an audit log surface yet?"
 * has nowhere to look — they'd have to grep the codebase. Showing
 * dimmed cards with `coming soon` keeps the discovery surface
 * complete without misleading users about what they can click.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ADMIN_SECTIONS } from './sections.js'
import { cn } from '@/lib/utils'
import { VersionInfo } from '@/components/settings/VersionInfo'
import { adminModelVerificationAPI } from '@/lib/api'

/** Which section paths show a "Not working" red dot on the discovery
 * card. Today this is computed only for the Models section (#1281);
 * the keys are an explicit map so future sections can wire in their
 * own health signals without rewiring the discovery layout. */
function useDiscoveryBadges(): Set<string> {
  const [badged, setBadged] = useState<Set<string>>(new Set())
  useEffect(() => {
    let cancelled = false
    void adminModelVerificationAPI
      .list()
      .then((r) => {
        if (cancelled) return
        const next = new Set<string>()
        if (r.anyNotWorking) next.add('/admin/models')
        setBadged(next)
      })
      .catch(() => {
        // Discovery badge is a soft signal; on fetch failure, leave
        // the badges empty rather than throw an error toast.
      })
    return () => {
      cancelled = true
    }
  }, [])
  return badged
}

export function AdminPage() {
  const badgedPaths = useDiscoveryBadges()
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Global Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Operational tools for OttoTester operators. Pick a section
          below to drill in.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ADMIN_SECTIONS.map((s) => {
          const Icon = s.icon
          const card = (
            <div
              className={cn(
                'border rounded-lg p-5 h-full transition-colors',
                s.status === 'shipped'
                  ? 'hover:bg-muted/40 hover:border-primary/40'
                  : 'opacity-60',
              )}
            >
              <div className="flex items-start gap-3">
                <Icon
                  className={cn(
                    'h-7 w-7 flex-shrink-0',
                    s.status === 'shipped' ? s.iconColor : 'text-muted-foreground',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-semibold leading-none">{s.label}</h2>
                    {s.status === 'unbuilt' && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-muted text-muted-foreground">
                        Not available yet
                      </span>
                    )}
                    {badgedPaths.has(s.path) && (
                      <span
                        aria-label="One or more items in this section need attention"
                        className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-destructive"
                      >
                        <span className="h-2 w-2 rounded-full bg-destructive" />
                        Needs attention
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {s.description}
                  </p>
                  {s.children && s.children.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {s.children.map((c) => (
                        <li key={c.path} className="flex items-center gap-1">
                          <span aria-hidden="true">·</span>
                          <span className="font-medium text-foreground/80">{c.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.status === 'unbuilt' && s.todo && (
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3">
                      Tracked: {s.todo}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
          return s.status === 'shipped' ? (
            <Link
              key={s.path}
              to={s.path}
              className="block focus:outline-none focus:ring-2 focus:ring-ring rounded-lg"
            >
              {card}
            </Link>
          ) : (
            // Unbuilt cards stay non-interactive — no Link wrapper, no
            // cursor-pointer affordance. The "Coming soon" badge is
            // the only signal the user needs.
            <div key={s.path}>{card}</div>
          )
        })}
      </div>

      {/* Build metadata for this environment (ottotester#683).
          Operator-facing, so it lives behind this page's superadmin
          gate rather than in Settings, where every signed-in user
          would see it. /api/build-info stays public for smoke tests
          and the operator `curl` playbook — only the display moved. */}
      <div className="border-t pt-6">
        <h2 className="text-base font-semibold mb-3">Build</h2>
        <VersionInfo />
      </div>
    </div>
  )
}
